import React, { ForwardedRef, forwardRef, useEffect, useMemo, useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  YAxisProps,
  CartesianGrid,
  Cell,
} from 'recharts'
import { Box, Typography, useMediaQuery } from '@mui/material'
import styles from 'commonStyles/removeCartesianGrid.module.css'
import commonStyles from 'commonStyles/index.module.css'
import { GROUPING_LIST, INCOME_GROUP_INFO, PILLAR_INFO, REGION_INFO } from 'constant'
import { calculateStringWidth, isDataKeyExistsInData } from 'utils'
import { useActions } from 'hooks'

export interface BarChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[] | undefined
  isStackedBarChart: boolean
  xAxisTicks?: (string | number)[] | undefined
  yAxisTickRenderFunc?: YAxisProps['tick']
  tooltipContent?: string
  CustomToolTipComponent?: any
  CustomLegendComponent?: (payload: any) => React.JSX.Element | undefined
  onClickBarChartEvent?: (data: any) => void
  is4Downloaded?: boolean
  configuration?: {
    currentGrouping?: string
  }
}

const BarChartComponent = forwardRef(
  (
    {
      data,
      isStackedBarChart,
      xAxisTicks,
      yAxisTickRenderFunc,
      tooltipContent,
      CustomLegendComponent,
      CustomToolTipComponent,
      onClickBarChartEvent,
      is4Downloaded,
      configuration: { currentGrouping = '' } = { currentGrouping: '' },
    }: BarChartProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const groupingInfo = GROUPING_LIST.find((grouping) => grouping.displayName === currentGrouping)!
    const [hovered, setHovered] = useState(false)
    const [focusCell, setFocusCell] = useState<number | null>(null)
    const yAxisWidth = useMemo(() => {
      if (!data || data.length === 0) return 0
      const countryNameWidths = data.map((item) =>
        calculateStringWidth(item.countryName, 'normal 16px "CircularStdMedium", sans-serif'),
      )
      const maxWidth = Math.max(...countryNameWidths)

      return maxWidth + 40
    }, [data])

    const calculateLegends = (currentGroupingParam: string, dataParam: any[] | undefined) => {
      if (!dataParam) return undefined
      switch (currentGroupingParam) {
        case 'Pillar':
          return PILLAR_INFO.filter((el) => isDataKeyExistsInData(dataParam, el)).map((pillar) => ({
            id: pillar.dataKey,
            type: 'square' as 'square',
            value: pillar.displayName,
            color: pillar.color,
          }))
        case 'Region':
          return REGION_INFO.map((region) => ({
            id: region.displayName,
            type: 'square' as 'square',
            value: region.displayName,
            color: region.color,
          }))
        case 'Income Group':
          return INCOME_GROUP_INFO.map((incomeGroup) => ({
            id: incomeGroup.displayName,
            type: 'square' as 'square',
            value: incomeGroup.displayName,
            color: incomeGroup.color,
          }))
        default:
          return undefined
      }
    }

    const calculateCellColors = (currentGroupingParam: string, dataEl: any, isHover: boolean) => {
      if (!dataEl) return undefined
      switch (currentGroupingParam) {
        case 'Region':
          return isHover
            ? REGION_INFO.find((region) => dataEl[groupingInfo.name] === region.name)?.lightColor
            : REGION_INFO.find((region) => dataEl[groupingInfo.name] === region.name)?.color
        case 'Income Group':
          return isHover
            ? INCOME_GROUP_INFO.find((incomeGroup) => dataEl[groupingInfo.name] === incomeGroup.name)?.lightColor
            : INCOME_GROUP_INFO.find((incomeGroup) => dataEl[groupingInfo.name] === incomeGroup.name)?.color
        default:
          return undefined
      }
    }

    const legendList = calculateLegends(currentGrouping, data)
    const isSmallerThan1536 = useMediaQuery('(max-width:1536px)')
    const isSmallerThan1260 = useMediaQuery('(max-width:1260px)')

    const breakpoints = useMemo(() => {
      switch (currentGrouping) {
        case 'Region': {
          return {
            screen: true,
            grid: 200,
          }
        }
        case 'Income Group': {
          return {
            screen: isSmallerThan1260,
            grid: 160,
          }
        }

        default:
          return {
            screen: isSmallerThan1536,
            grid: 168,
          }
      }
    }, [currentGrouping, isSmallerThan1536, isSmallerThan1260])

    return (
      <>
        <Box
          display={breakpoints.screen ? 'grid' : 'flex'}
          sx={{
            padding: '8px 0 16px 0',
            justifyContent: currentGrouping !== 'Income Group' ? 'space-between' : undefined,
            gap: currentGrouping === 'Income Group' ? (isSmallerThan1260 ? '10px' : '15px') : '5px',
            gridTemplateColumns: breakpoints.screen
              ? `repeat(auto-fill, minmax(${breakpoints.grid}px, 1fr))`
              : undefined,
          }}
        >
          {legendList?.map((l) => (
            <Box
              display={'flex'}
              flexDirection={'row'}
              flexWrap={'nowrap'}
              alignItems={'center'}
              gap={'6px'}
              key={l.id}
            >
              <Box sx={{ width: 14, height: 14, backgroundColor: l.color }}></Box>
              <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{l.value}</span>
            </Box>
          ))}
        </Box>
        <Box
          sx={{ overflowY: 'auto', overflowX: is4Downloaded ? 'hidden' : 'auto', mb: '16px' }}
          className={is4Downloaded ? undefined : commonStyles['scrollbar-common']}
          ref={ref}
        >
          <ResponsiveContainer minWidth='900px' width='100%' height={(data ?? []).length * 39} className={styles.grid}>
            <BarChart
              layout='vertical'
              data={data}
              margin={{
                top: 5,
                right: 24,
                left: 10,
                bottom: 5,
              }}
              barSize={20}
            >
              <XAxis
                type='number'
                tickLine={false}
                axisLine={false}
                domain={[0, 'dataMax + 2']}
                allowDataOverflow
                ticks={xAxisTicks}
              />
              <YAxis
                dataKey='name'
                tickLine={false}
                axisLine={false}
                type='category'
                tick={yAxisTickRenderFunc}
                width={yAxisWidth}
                fontFamily='CircularStdBook'
              />
              <CartesianGrid horizontal={false} />
              <Tooltip
                content={CustomToolTipComponent ? <CustomToolTipComponent show={true} /> : undefined}
                // labelFormatter={tooltipContent ? () => `${tooltipContent}` : undefined}
                // formatter={(value, name) => [parseFloat(value as string).toFixed(2), name]}
                cursor={{ fill: 'transparent' }}
              />
              {/* <Legend
              content={CustomLegendComponent ? CustomLegendComponent : undefined}
              layout='vertical'
              verticalAlign='bottom'
              align='right'
              formatter={(value) => <span style={{ color: 'black' }}>{value}</span>}
              payload={calculateLegends(currentGrouping, data)}
            /> */}
              {!isStackedBarChart && (
                <Bar
                  cursor='pointer'
                  dataKey='score'
                  fill='rgb(36, 148, 153)'
                  activeBar={{ fill: hovered ? 'rgb(36, 148, 153, 0.8)' : 'rgb(36, 148, 153)' }}
                  onMouseOver={() => {
                    setHovered(true)
                  }}
                  onMouseOut={() => {
                    setHovered(false)
                  }}
                  onClick={(dataOnClick) => {
                    onClickBarChartEvent && onClickBarChartEvent(dataOnClick)
                  }}
                  isAnimationActive={false}
                />
              )}
              {isStackedBarChart && currentGrouping === 'Pillar' && (
                <>
                  {PILLAR_INFO.filter((el) => {
                    return isDataKeyExistsInData(data, el)
                  }).map((pillar) => (
                    <Bar
                      cursor='pointer'
                      key={pillar.dataKey}
                      dataKey={pillar.dataKey}
                      name={pillar.displayName}
                      fill={pillar.color}
                      activeBar={{ fill: hovered ? pillar.lightColor : pillar.color }}
                      stackId='a'
                      onMouseOver={() => {
                        setHovered(true)
                      }}
                      onMouseOut={() => {
                        setHovered(false)
                      }}
                      onClick={(dataOnClick) => {
                        onClickBarChartEvent && onClickBarChartEvent(dataOnClick)
                      }}
                      style={{ stroke: '#fff', strokeWidth: 1 }}
                      isAnimationActive={false}
                    />
                  ))}
                </>
              )}
              {isStackedBarChart && currentGrouping && currentGrouping !== 'Pillar' && (
                <>
                  <Bar
                    dataKey='score'
                    onMouseOver={() => {
                      setHovered(true)
                    }}
                    onMouseOut={() => {
                      setHovered(false)
                    }}
                    isAnimationActive={false}
                  >
                    {data!.map((entry, index) => (
                      <Cell
                        cursor='pointer'
                        fill={calculateCellColors(currentGrouping, entry, focusCell === index)}
                        // fill={'red'}
                        key={`cell-${index}`}
                        onMouseOver={() => {
                          setFocusCell(index)
                          setHovered(true)
                        }}
                        onMouseOut={() => {
                          setHovered(false)
                          setFocusCell(null)
                        }}
                        onClick={() => {
                          onClickBarChartEvent && onClickBarChartEvent({ name: entry.name })
                        }}
                      />
                    ))}
                  </Bar>
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
          <Box sx={{ padding: '24px' }}>
            <Typography align='center' sx={{ fontSize: { xs: '12px', sm: '16px' } }}>
              Total Weighted Index Score
            </Typography>
          </Box>
        </Box>
      </>
    )
  },
)

BarChartComponent.displayName = 'BarChartComponent'

export default BarChartComponent
